import { apiAdmin } from "./api";
import { mapCalendarItem, getCategoryApiValue } from "../utils/agendaUtils";

/**
 * Fetch calendar list from backend API (/endpoint/user/calendar)
 * @param {Object} options
 * @param {string} options.selectedCategory - Frontend category ('all', 'webinar', 'clinical_session', 'event', 'mentorship')
 * @param {string} options.entityType - Optional backend-ready entity type value, such as 'webinar,event'
 * @param {"calendar"|"saved"|"completed"} options.collection - Source collection to load
 * @param {number} options.page - Page number
 * @param {number} options.perPage - Items per page
 */
export const getCalendarEvents = async ({
  selectedCategory,
  entityType,
  event_type,
  page = 1,
  perPage = 16,
  collection = "calendar",
  keyword,
} = {}) => {
  try {
    const rawCategoryOrType =
      event_type !== undefined
        ? event_type
        : entityType !== undefined
        ? entityType
        : selectedCategory !== undefined
        ? selectedCategory
        : "event,webinar";

    const resolvedEntityType = getCategoryApiValue(rawCategoryOrType, collection) || "event,webinar";

    const isSavedOrCompleted = collection === "saved" || collection === "completed";
    const params = {
      paginate: 1,
      per_page: perPage,
      limit: perPage,
      page,
      entity_type: resolvedEntityType,
    };

    if (keyword && String(keyword).trim()) {
      params.keyword = String(keyword).trim();
    }

    const endpoint = isSavedOrCompleted
      ? `user/calendar/${collection}`
      : "user/calendar";
    const response = await apiAdmin.get(endpoint, { params });

    if (response?.data?.status) {
      const resPayload = response.data.data ?? response.data ?? {};
      const listContainer = resPayload.list ?? resPayload;
      const rawListData = listContainer?.data ?? listContainer ?? [];
      const rawList = Array.isArray(rawListData)
        ? rawListData
        : rawListData && typeof rawListData === "object"
        ? Object.values(rawListData)
        : [];
      const mappedEvents = rawList.map((item) => mapCalendarItem(item, collection)).filter(Boolean);

      // Extract direct upcoming array from backend response if present
      const rawUpcoming = resPayload.upcoming ?? [];
      const rawUpcomingList = Array.isArray(rawUpcoming)
        ? rawUpcoming
        : rawUpcoming && typeof rawUpcoming === "object"
        ? Object.values(rawUpcoming)
        : [];
      const mappedUpcoming = rawUpcomingList.map((item) => mapCalendarItem(item, collection)).filter(Boolean);

      // Extract suggestions array from backend response if present
      const rawSuggestion = resPayload.suggestion ?? [];
      const rawSuggestionList = Array.isArray(rawSuggestion)
        ? rawSuggestion
        : rawSuggestion && typeof rawSuggestion === "object"
        ? Object.values(rawSuggestion)
        : [];
      const mappedSuggestion = rawSuggestionList.map((item) => mapCalendarItem(item, collection)).filter(Boolean);

      // Extract ads object from backend response
      const rawAds = resPayload.ads ?? response.data?.ads ?? null;

      return {
        status: true,
        data: mappedEvents,
        upcoming: mappedUpcoming,
        suggestion: mappedSuggestion,
        ads: rawAds,
        raw: resPayload,
        pagination: {
          currentPage: Number(listContainer?.current_page || page),
          lastPage: Number(listContainer?.last_page || 1),
          total: Number(listContainer?.total || mappedEvents.length),
          nextPageUrl: listContainer?.next_page_url || null,
        },
      };
    }

    return {
      status: false,
      data: [],
      error: response?.data?.message || "Failed to fetch calendar events",
      pagination: {
        currentPage: 1,
        lastPage: 1,
        total: 0,
        nextPageUrl: null,
      },
    };
  } catch (error) {
    console.error("Error in getCalendarEvents:", error);
    throw error;
  }
};

/**
 * Load the complete event record before opening the agenda edit drawer.
 * Calendar responses intentionally contain only the fields needed for a card.
 */
export const getAgendaEventDetail = async (eventId, type = "event") => {
  if (!eventId) {
    throw new Error("An event id is required to load event details");
  }

  const rawId = String(eventId);
  const cleanId = rawId.replace(/^(?:session-|evt-|event-|webinar-)/i, "");

  const isWebinar =
    type === "webinar" ||
    type === "session" ||
    type === "clinical_session" ||
    /^webinar-/i.test(rawId) ||
    /^session-/i.test(rawId);

  const endpoint = isWebinar
    ? `user/post/detail/${cleanId}`
    : `user/event/detail/${cleanId}`;

  try {
    const response = await apiAdmin.get(endpoint);
    const responseData = response?.data;

    if (responseData?.status && responseData?.data) {
      return responseData.data;
    } else if (responseData?.data) {
      return responseData.data;
    }

    throw new Error(
      responseData?.action ||
        responseData?.message ||
        "Unable to load event details"
    );
  } catch (error) {
    console.error(`Error loading agenda ${isWebinar ? "webinar" : "event"} details:`, error);
    throw error;
  }
};

/**
 * Send Webinar Invitation
 */
export const sendWebinarInvite = async (webinarId, userId) => {
  try {
    const response = await apiAdmin.get(`user/post/webinar/invite/${webinarId}/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error sending webinar invite:", error);
    throw error;
  }
};

/**
 * Send Clinical Session Invitation
 */
export const sendClinicalSessionInvite = async (sessionId, userId) => {
  try {
    const response = await apiAdmin.get(`user/post/clinical-session/invite/${sessionId}/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error sending clinical session invite:", error);
    throw error;
  }
};

/**
 * Toggle Event Favorite / Save (/endpoint/user/event/favorite/:id)
 * Endpoint: user/event/favorite/:eventId
 * Note: Only applicable for events.
 * @param {string|number} eventId
 */
export const toggleFavoriteEvent = async (eventId) => {
  if (!eventId) {
    throw new Error("An event id is required to toggle favorite status");
  }
  const cleanId = String(eventId).replace(/^(?:evt-|event-)/i, "");
  try {
    const response = await apiAdmin.get(`user/event/favorite/${cleanId}`);
    return response.data;
  } catch (error) {
    console.error(`Error toggling favorite for event ${cleanId}:`, error);
    throw error;
  }
};

/**
 * Fetch users for featured speakers
 * GET /user/event/feature-speakers/users/:eventId?page=:page
 * @param {string|number} eventId
 * @param {number} page
 */
export const getEventFeatureSpeakersUsers = async (eventId, page = 1) => {
  const cleanId = String(eventId || 1).replace(/^(?:session-|evt-|event-|webinar-)/i, "");
  try {
    const response = await apiAdmin.get(`user/event/feature-speakers/users/${cleanId}`, {
      params: { page },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching featured speakers users for event ${cleanId}:`, error);
    throw error;
  }
};

/**
 * Send Feature Speakers Invite
 * POST /user/event/feature-speakers/invite
 * @param {string|number} eventId
 * @param {string|Array<string|number>} userIds
 */
export const sendEventFeatureSpeakersInvite = async (eventId, userIds) => {
  const cleanId = String(eventId || 1).replace(/^(?:session-|evt-|event-|webinar-)/i, "");
  const userIdsStr = Array.isArray(userIds) ? userIds.join(",") : String(userIds);

  const formData = new FormData();
  formData.append("event_id", cleanId);
  formData.append("user_ids", userIdsStr);

  try {
    const response = await apiAdmin.post("user/event/feature-speakers/invite", formData);
    return response.data;
  } catch (error) {
    console.error(`Error sending feature speakers invite for event ${cleanId}:`, error);
    throw error;
  }
};

/**
 * Add Event Agenda Session
 * POST /user/event/agenda/add
 * @param {Object} payload
 * @param {string|number} payload.eventId
 * @param {string} payload.title
 * @param {string} payload.startTime
 * @param {string|number} payload.startTimestamp
 * @param {string|number} payload.duration
 * @param {string} [payload.presented_by]
 */
export const addEventAgendaSession = async ({
  eventId,
  title,
  startTime,
  startTimestamp,
  duration,
  presented_by,
}) => {
  const cleanEventId = String(eventId || 1).replace(/^(?:session-|evt-|event-|webinar-)/i, "");
  const formData = new FormData();
  formData.append("event_id", cleanEventId);
  formData.append("title", title || "Session");
  formData.append("start_time", startTime || "09:00");
  formData.append("start_timestamp", String(startTimestamp || Math.floor(Date.now() / 1000)));
  formData.append("duration", String(duration || 20));
  formData.append("presented_by", presented_by || "");

  try {
    const response = await apiAdmin.post("user/event/agenda/add", formData);
    return response.data;
  } catch (error) {
    console.error(`Error adding agenda session for event ${cleanEventId}:`, error);
    throw error;
  }
};

/**
 * Update Event Agenda Session
 * POST /user/event/agenda/update
 * @param {Object} payload
 * @param {string|number} payload.sessionId
 * @param {string} payload.title
 * @param {string} payload.startTime
 * @param {string|number} payload.startTimestamp
 * @param {string|number} payload.duration
 * @param {string} [payload.presented_by]
 */
export const updateEventAgendaSession = async ({
  sessionId,
  title,
  startTime,
  startTimestamp,
  duration,
  presented_by,
}) => {
  const cleanSessionId = String(sessionId).replace(/^(?:session-|evt-|event-|webinar-)/i, "");
  const formData = new FormData();
  formData.append("session_id", cleanSessionId);
  formData.append("title", title || "Session");
  formData.append("start_time", startTime || "09:00");
  formData.append("start_timestamp", String(startTimestamp || Math.floor(Date.now() / 1000)));
  formData.append("duration", String(duration || 20));
  formData.append("presented_by", presented_by || "");

  try {
    const response = await apiAdmin.post("user/event/agenda/update", formData);
    return response.data;
  } catch (error) {
    console.error(`Error updating agenda session ${cleanSessionId}:`, error);
    throw error;
  }
};

/**
 * Delete Event Agenda Session
 * GET /user/event/agenda/delete/:sessionId
 * @param {string|number} sessionId
 */
export const deleteEventAgendaSession = async (sessionId) => {
  const cleanSessionId = String(sessionId).replace(/^(?:session-|evt-|event-|webinar-)/i, "");
  try {
    const response = await apiAdmin.get(`user/event/agenda/delete/${cleanSessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting agenda session ${cleanSessionId}:`, error);
    throw error;
  }
};



